/**
 * INSTRUCTOR SLIDER — Web Component (Impact Theme Standard)
 * Self-contained slider with INP-optimized scroll handling,
 * viewport-aware autoplay, and proper cleanup.
 */

class InstructorSlider extends HTMLElement {
  constructor() {
    super();
    this._rafId = null;
    this._scrollTimeout = null;
    this._autoPlayInterval = null;
    this._totalRealCards = 0;
    this._cloneCount = 5;
    this._cachedCardWidth = 0;
  }

  connectedCallback() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this._init());
    } else {
      setTimeout(() => this._init(), 1);
    }
  }

  _init() {
    this.slider = this.querySelector('.instructor-slider');
    this.dotsContainer = this.querySelector('.instructor-dots');
    this.prevBtn = this.querySelector('.instructor-slider-prev');
    this.nextBtn = this.querySelector('.instructor-slider-next');
    this.wrapper = this.querySelector('.instructor-slider-wrapper');

    if (!this.slider || !this.dotsContainer || !this.wrapper) return;
    if (this.wrapper.classList.contains('is-static')) return;

    this.dots = this.dotsContainer.querySelectorAll('.instructor-dot');
    this.cards = this.slider.querySelectorAll('.instructor-card');
    this._totalRealCards = parseInt(this.dataset.count, 10) || 0;

    // Cache card width & update on resize
    this._cacheCardWidth();
    this._resizeObserver = new ResizeObserver(() => this._cacheCardWidth());
    this._resizeObserver.observe(this.slider);

    this._initScroll();
    this._bindEvents();
    this._initViewportObserver();
  }

  // Cache cardWidth — chỉ đọc offsetWidth khi layout thực sự thay đổi
  _cacheCardWidth() {
    this._cachedCardWidth = this.cards[0]?.offsetWidth || 1;
  }

  _bindEvents() {
    this.slider.addEventListener('scroll', () => {
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this._rafId = requestAnimationFrame(() => this._onScrollFrame());
    }, { passive: true });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const targetRealIndex = parseInt(dot.dataset.index, 10);
        this._goToSlide(targetRealIndex);
        this._restartAutoPlay();
      });
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this._prevSlide();
        this._restartAutoPlay();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this._nextSlide();
        this._restartAutoPlay();
      });
    }

    if (this.wrapper) {
      this.wrapper.addEventListener('mouseenter', () => this._stopAutoPlay());
      this.wrapper.addEventListener('mouseleave', () => this._startAutoPlay());
    }
  }

  _onScrollFrame() {
    this._rafId = null;
    this._checkLoop();

    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => this._updateActiveDot(), 80);
  }

  // Dùng cached width — không đọc offsetWidth mỗi frame
  _checkLoop() {
    const scrollLeft = this.slider.scrollLeft;
    const cardWidth = this._cachedCardWidth;
    const totalReal = this._totalRealCards;
    const clones = this._cloneCount;

    if (scrollLeft <= cardWidth * (clones - 1)) {
      this.slider.classList.add('no-smooth');
      this.slider.scrollLeft = scrollLeft + (cardWidth * totalReal);
      requestAnimationFrame(() => this.slider.classList.remove('no-smooth'));
    } else if (scrollLeft >= cardWidth * (totalReal + clones)) {
      this.slider.classList.add('no-smooth');
      this.slider.scrollLeft = scrollLeft - (cardWidth * totalReal);
      requestAnimationFrame(() => this.slider.classList.remove('no-smooth'));
    }
  }

  _updateActiveDot() {
    const scrollLeft = this.slider.scrollLeft;
    const cardWidth = this._cachedCardWidth;
    const totalReal = this._totalRealCards;
    const clones = this._cloneCount;

    let activeRealIndex = Math.round(scrollLeft / cardWidth) - clones;
    activeRealIndex = ((activeRealIndex % totalReal) + totalReal) % totalReal;

    const activeGroupIndex = Math.floor(activeRealIndex / 5);

    requestAnimationFrame(() => {
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeGroupIndex);
      });
    });
  }

  _goToSlide(index) {
    const targetIndex = index + this._cloneCount;
    const targetCard = this.cards[targetIndex];
    if (targetCard) {
      this.slider.scrollTo({
        left: targetCard.offsetLeft - this.slider.offsetLeft,
        behavior: 'smooth',
      });
    }
  }

  _nextSlide() {
    const cardWidth = this._cachedCardWidth;
    const currentIdx = Math.round(this.slider.scrollLeft / cardWidth);
    this._goToSlide(currentIdx - this._cloneCount + 1);
  }

  _prevSlide() {
    const cardWidth = this._cachedCardWidth;
    const currentIdx = Math.round(this.slider.scrollLeft / cardWidth);
    this._goToSlide(currentIdx - this._cloneCount - 1);
  }

  _initScroll() {
    const cardWidth = this._cachedCardWidth;
    this.slider.classList.add('no-smooth');
    this.slider.scrollLeft = cardWidth * this._cloneCount;
    requestAnimationFrame(() => this.slider.classList.remove('no-smooth'));
  }

  _initViewportObserver() {
    this._visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._startAutoPlay();
          } else {
            this._stopAutoPlay();
          }
        });
      },
      { threshold: 0.1 }
    );
    this._visibilityObserver.observe(this);
  }

  _startAutoPlay() {
    this._stopAutoPlay();
    this._autoPlayInterval = setInterval(() => this._nextSlide(), 5000);
  }

  _stopAutoPlay() {
    if (this._autoPlayInterval) {
      clearInterval(this._autoPlayInterval);
      this._autoPlayInterval = null;
    }
  }

  _restartAutoPlay() {
    this._stopAutoPlay();
    this._startAutoPlay();
  }

  disconnectedCallback() {
    this._stopAutoPlay();
    if (this._visibilityObserver) this._visibilityObserver.disconnect();
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._rafId) cancelAnimationFrame(this._rafId);
    clearTimeout(this._scrollTimeout);
  }
}

if (!customElements.get('instructor-slider')) {
  customElements.define('instructor-slider', InstructorSlider);
}
